import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/design/design/node_modules/@primer/gatsby-theme-doctocat/src/components/layout.js";
import { Box } from '@primer/react';
export const _frontmatter = {};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const CustomVideoPlayer = makeShortcode("CustomVideoPlayer");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <p>{`This documentation provides an overview of navigation design patterns within Primer. For more granular guidance about the components and insights into their implementation, reference their respective guidelines pages.`}</p>
    <h2>{`Best practices`}</h2>
    <p>{`Prioritize clarity and simplicity to facilitate seamless user interaction.`}</p>
    <h3>{`Clear user orientation`}</h3>
    <p>{`Users should always have a clear understanding of their location within the application. Intuitive navigational cues minimize ambiguity.`}</p>
    <h3>{`Streamlined choices`}</h3>
    <p>{`Minimize the number of navigational elements while ensuring that the most essential items are available. This balance can help prevent users from feeling overwhelmed with too many options, but also ensure that they can easily find what they need.`}</p>
    <h3>{`Contextual transitions`}</h3>
    <p>{`Strive to maintain a balance between contextual clarity and minimal interruption. While transitions such as page refreshes or showing a dialog are useful for indicating context switches, excessive transitions can lead to unnecessary cognitive load.`}</p>
    <h2>{`Contextual shifts and transitions`}</h2>
    <h3>{`Entirely new page`}</h3>
    <Box display="flex" flexDirection={['column', 'column', 'column', 'column', 'row']} sx={{
      alignItems: 'flex-start',
      gap: 3
    }} mdxType="Box">
      <Box flex={1} mdxType="Box">
        <p>{`This is the default behavior of clicking a link or submitting a form. The URL changes and the requested page loads.`}</p>
        {/* TODO: ask an engineer to confirm this is technically accurate */}
        <p>{`In GitHub's single-page app architecture, this behavior is emulated without a traditional page reload.`}</p>
        <p>{`If you're navigating from a parent page to a child page, the child page should have a clear way to navigate back to the parent page. This is often done with `}<a parentName="p" {...{
            "href": "/components/breadcrumbs"
          }}>{`breadcrumbs`}</a>{` or a "back" button.`}</p>
      </Box>
      <CustomVideoPlayer width="456" loop src="https://github.com/primer/design/assets/2313998/6d8008b5-bc5a-4f21-ba14-ac57550b2de3" mdxType="CustomVideoPlayer" />
    </Box>
    <p>{`For example, when adding a new Codespace secret, the user is taken to a new page to fill out the secret details. There is a breadcrumb at the top of the page letting them go back to the Codespace settings page.`}</p>
    <p>{`Be mindful of data loss when a user is navigating away from a page where they have unsaved changes. See the `}<a parentName="p" {...{
        "href": "/ui-patterns/saving"
      }}>{`saving`}</a>{` guidelines for more info on handling unsaved changes.`}</p>
    <h3>{`Same page, different state`}</h3>
    <Box display="flex" flexDirection={['column', 'column', 'column', 'column', 'row']} sx={{
      alignItems: 'flex-start',
      gap: 3
    }} mdxType="Box">
      <Box flex={1} mdxType="Box">
        <p>{`Example: user activated a different state of their current page. `}</p>
        <p>{`When using this kind of navigation, strive to avoid full page reloads when feasible. This could help maintain a fast and smooth user experience.`}</p>
        <p>{`The URL may or may not change.`}</p>
      </Box>
      <CustomVideoPlayer width="456" loop src="https://github.com/primer/design/assets/2313998/bbd4675d-546f-4f7d-9736-8b6419ed54c5" mdxType="CustomVideoPlayer" />
    </Box>
    <h3>{`New spot on the same page`}</h3>
    <Box display="flex" flexDirection={['column', 'column', 'column', 'column', 'row']} sx={{
      alignItems: 'flex-start',
      gap: 3
    }} mdxType="Box">
      <Box flex={1} mdxType="Box">
        <p>{`Example: User navigated to a different section or moved focus to a specific element within the same page. `}</p>
        <p>{`The page content is not changed. This behavior is commonly used for scrolling to new sections or focusing on input fields.`}</p>
        <p>{`The URL should change if the section scrolled to is a page anchor.`}</p>
      </Box>
      <CustomVideoPlayer width="456" loop src="https://github.com/primer/design/assets/2313998/c4e4672a-11a6-4444-aaa6-f8f2b032f11b" mdxType="CustomVideoPlayer" />
    </Box>
    <h3>{`Moving into a dialog`}</h3>
    <Box display="flex" flexDirection={['column', 'column', 'column', 'column', 'row']} sx={{
      alignItems: 'flex-start',
      gap: 3
    }} mdxType="Box">
      <Box flex={1} mdxType="Box">
        <p>{`Opening a dialog brings the user into a new context, but can feel "lighter" than going to a whole new page. Focus is moved into the dialog, and the user can't interact with the rest of the page without dismissing the dialog.`}</p>
        <p>{`The URL may or may not change when opening a dialog.`}</p>
      </Box>
      <CustomVideoPlayer width="456" loop src="https://github.com/primer/design/assets/2313998/ec911107-016e-4d64-9d08-c235b19a32cb" mdxType="CustomVideoPlayer" />
    </Box>
    <h2>{`Patterns and components`}</h2>
    <h3>{`Nav list`}</h3>
    <Box display="flex" flexDirection={['column', 'column', 'column', 'column', 'row']} sx={{
      alignItems: 'flex-start',
      gap: 3
    }} mdxType="Box">
      <Box flex={1} mdxType="Box">
        <p>{`A vertical list of links that can be used to implement a parent-detail navigation pattern. Users can navigate between different child views without leaving the parent page. It's often used in the sidebar of a split page layout.`}</p>
        <p>{`Activating a nav list item should change the URL.`}</p>
        <p><a parentName="p" {...{
            "href": "/components/nav-list"
          }}>{`Nav list component guidelines`}</a></p>
      </Box>
      <img width="456" alt="A nav list component" src="https://github.com/primer/design/assets/2313998/ce7949ad-d9d7-4f07-a531-73c074ff6b54" />
    </Box>
    <h3>{`Tree view`}</h3>
    <Box display="flex" flexDirection={['column', 'column', 'column', 'column', 'row']} sx={{
      alignItems: 'flex-start',
      gap: 3
    }} mdxType="Box">
      <Box flex={1} mdxType="Box">
        <p>{`A tree view isn't semantically a form of navigation: it's for activating an option from a hierarchical list. However, it's often used to implement a parent-detail navigation pattern. It's often used in the sidebar of a split page layout.`}</p>
        <p>{`Activating a tree view item may or may not change the URL.`}</p>
        <p><a parentName="p" {...{
            "href": "/components/tree-view"
          }}>{`Tree view component guidelines`}</a></p>
      </Box>
      <img width="456" alt="A tree view component" src="https://github.com/primer/design/assets/2313998/ac179492-b53e-4211-a61a-0c4605808c64" />
    </Box>
    <h3>{`Tabs`}</h3>
    <Box display="flex" flexDirection={['column', 'column', 'column', 'column', 'row']} sx={{
      alignItems: 'flex-start',
      gap: 3
    }} mdxType="Box">
      <Box flex={1} mdxType="Box">
        <p>{`Tabs allow users to switch between related views within a consistent context, enhancing navigation efficiency and organization.`}</p>
        <p>{`Use the `}<a parentName="p" {...{
            "href": "/components/underline-nav"
          }}>{`underline nav`}</a>{` component when activating a tab changes the URL.`}</p>
        <p>{`Use the `}<a parentName="p" {...{
            "href": "/components/underline-panels"
          }}>{`underline panels`}</a>{` component when activating a tab changes the content in the same page or context without changing the URL.`}</p>
        <p>{`Activating a tab may or may not change the URL, but you can't mix tabs that change the URL with tabs just switch the visible tab panel.`}</p>
        <p><a parentName="p" {...{
            "href": "/components/underline-nav"
          }}>{`Underline nav component guidelines`}</a></p>
        <p><a parentName="p" {...{
            "href": "/components/underline-panels"
          }}>{`Underline panel component guidelines`}</a></p>
      </Box>
      <img width="456" alt="An underline nav component highlighted on a repository page" src="https://github.com/primer/design/assets/2313998/f287c9dc-fb4c-401e-aa2e-5ca7e30ea27d" />
    </Box>
    <h3>{`Action menu`}</h3>
    <Box display="flex" flexDirection={['column', 'column', 'column', 'column', 'row']} sx={{
      alignItems: 'flex-start',
      gap: 3
    }} mdxType="Box">
      <Box flex={1} mdxType="Box">
        <p>{`An action menu is not semantically a form of navigation: it's for activating an option from a list. However, it may contain some options that open a dialog or change the URL when activated.`}</p>
        <p>{`Action menus are also used to overflow horizontal navigation elements (such as tabs) when the container is too narrow to display all options inline.`}</p>
        <p>{`Activating an action menu item may or may not change the URL.`}</p>
        <p><a parentName="p" {...{
            "href": "/components/action-menu"
          }}>{`Action menu component guidelines`}</a></p>
      </Box>
      <img width="456" alt="Screenshot of '+' action menu expanded in global navigation" src="https://github.com/primer/design/assets/2313998/f8efba93-2a4f-43f6-b428-d804b423c737" />
    </Box>
    <h3>{`Breadcrumbs`}</h3>
    <Box display="flex" flexDirection={['column', 'column', 'column', 'column', 'row']} sx={{
      alignItems: 'flex-start',
      gap: 3
    }} mdxType="Box">
      <Box flex={1} mdxType="Box">
        <p>{`Breadcrumbs show the user where they are using a hierarchical list of links. Going from start to end, each link is one level of hierarchy deeper.`}</p>
        <p>{`Activating a breadcrumb item should change the URL.`}</p>
        <p><a parentName="p" {...{
            "href": "/components/breadcrumbs"
          }}>{`Breadcrumbs component guidelines`}</a></p>
      </Box>
      <img width="456" alt="Screenshot of files page breadcrumb" src="https://github.com/primer/design/assets/2313998/2a8aa05b-b406-44f8-9f8d-5f621fcad221" />
    </Box>
    <h3>{`Pagination`}</h3>
    <Box display="flex" flexDirection={['column', 'column', 'column', 'column', 'row']} sx={{
      alignItems: 'flex-start',
      gap: 3
    }} mdxType="Box">
      <Box flex={1} mdxType="Box">
        <p>{`Pagination is used to sequentially navigate long collections of content that's been broken into smaller chunks.`}</p>
        <p>{`Activating a pagination item should change the URL.`}</p>
        <p><a parentName="p" {...{
            "href": "/components/pagination"
          }}>{`Pagination component guidelines`}</a></p>
      </Box>
      <img width="456" alt="Screenshot of issues page pagination" src="https://github.com/primer/design/assets/2313998/7052c7d9-1e7a-41c1-8854-f85630c92df2" />
    </Box>
    <h3>{`Segmented control`}</h3>
    <Box display="flex" flexDirection={['column', 'column', 'column', 'column', 'row']} sx={{
      alignItems: 'flex-start',
      gap: 3
    }} mdxType="Box">
      <Box flex={1} mdxType="Box">
        <p>{`While primarily used to make a selection from a short list, segmented controls can be employed as navigational components for `}<strong parentName="p">{`filtering`}</strong>{`, `}<strong parentName="p">{`sorting`}</strong>{`, or `}<strong parentName="p">{`viewing content in a different format`}</strong>{`.`}</p>
        <p>{`When being used for navigation, activating a segmented control option may or may not change the URL.`}</p>
        <p><a parentName="p" {...{
            "href": "/components/segmented-control"
          }}>{`Segmented control component guidelines`}</a></p>
      </Box>
      <img width="456" alt="Screenshot of code/blame/preview on .md files" src="https://github.com/primer/design/assets/2313998/0b1e308d-a351-4457-8214-d863d8f76635" />
    </Box>
    <h2>{`Layout`}</h2>
    <p>{`Layout is used to structure your content in a way that reflects the hierarchy of the navigation within the app. Navigational elements should be laid out in close proximity to the content they affect.`}</p>
    <p>{`For example, pagination should go directly below the collection it navigates.`}</p>
    <h3>{`Layouts for navigating in the same context`}</h3>
    <h4>{`Parent-detail split page layout`}</h4>
    <img width="960" alt="Screenshot of a repo's security advisories page with the sidebar and main areas highlighted" src="https://github.com/primer/design/assets/2313998/7d8bafeb-f971-45d2-ac0e-7850d7f81912" />
    <p>{`A "parent" sidebar with a nav list or tree view that affects "detail" content. The "parent" navigation remains on screen while the user navigates between different "detail" views.`}</p>
    <p>{`On narrow viewports, the parent list may be stacked above the detail view or turned into an action menu. See the `}<a parentName="p" {...{
        "href": "/foundations/layout#page-types"
      }}>{`responsive layout guidelines`}</a>{` for more info.`}</p>
    <h4>{`Tabbed interfaces`}</h4>
    <img width="960" alt="Screenshot of a repo's security advisories page with the tabs and tab panel areas highlighted" src="https://github.com/primer/design/assets/2313998/f7fb7251-f9bf-4d58-b6ec-16b66f3959da" />
    <p>{`Tabbed interfaces enable seamless navigation between different subpages within a unified context. Tabs go directly above the content they affect.`}</p>
    <h3>{`Headings and hierarchical page sections`}</h3>
    <img width="960" alt="Wireframe of a settings page with multiple nested sections" src="https://github.com/primer/design/assets/2313998/88dedda4-355c-48ba-af14-9c01934dd276" />
    <p>{`A `}<a parentName="p" {...{
        "href": "/components/page-header"
      }}>{`page header`}</a>{` is the title area at the top of the main content region that shows the user where they are. It may also to contain navigational elements like breadcrumbs or a link to go up a level of hierarchy.`}</p>
    <p>{`Use section headings to show the user where they are in the context of the page. These headings should be styled to reflect their hierarchy within the page. Page section headings should be marked up with the appropriate heading level (`}<inlineCode parentName="p">{`h3`}</inlineCode>{`–`}<inlineCode parentName="p">{`h6`}</inlineCode>{`).`}</p>
    <p>{`See our `}<a parentName="p" {...{
        "href": "/primitives/storybook/?path=/story/typography-functional--overview"
      }}>{`typographic style options`}</a>{` for heading styles.`}</p>
    <h2>{`Accessibility`}</h2>
    <h3>{`Skip links`}</h3>
    <p>{`Facilitate efficient navigation for screen reader users by including skip links to bypass dense redundant content and navigate directly to essential sections.`}</p>
    <p>{`Example for skipping redundant content: there is a skip link near the global navigation that allows users skip over all the global navigation items and go straight to the main content.`}</p>
    <p>{`Example for skipping dense content: there is a skip link near the header of a data table that allows users to skip over the large amount of content often rendered in a data table.`}</p>
    <h3>{`Tab order`}</h3>
    <p>{`Users should be able to tab through the navigation elements in a logical order. The tab order should reflect the visual order of the elements.`}</p>
    <h3>{`Scannable headings`}</h3>
    <p>{`A correct heading structure is critical for enabling users to navigate quickly within a page. Headings are by far the most common navigation technique for screen reader users.`}</p>
    <p>{`Follow our accessibility `}<a parentName="p" {...{
        "href": "/guides/accessibility/headings"
      }}>{`guidelines for headings`}</a>{`.`}</p>
    <h2>{`Related links`}</h2>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/components/action-menu"
        }}>{`Action menu`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/components/breadcrumbs"
        }}>{`Breadcrumbs`}</a>
        {/* markdownlint-disable GH002 "Avoid using generic link text" - "Link" is actually the name of the component */}
      </li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/components/link"
        }}>{`Link`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/components/pagination"
        }}>{`Pagination`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/components/segmented-control"
        }}>{`Segmented control`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/components/underline-nav"
        }}>{`Underline nav`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/components/underline-panels"
        }}>{`Underline panels`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/guides/accessibility/focus-management"
        }}>{`Accessibility - focus management`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/guides/accessibility/headings"
        }}>{`Accessibility - headings`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/guides/accessibility/links"
        }}>{`Accessibility - links`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/foundations/layout#page-types"
        }}>{`Responsive layout`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/patterns/saving"
        }}>{`Saving`}</a></li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      